/**
 * Defensive helpers around the global `window.Capacitor` bridge.
 *
 * The native iOS/Android runtimes call `window.Capacitor.triggerEvent(...)`
 * (see @capacitor/ios CapacitorBridge.swift and CDVPluginManager.m) during
 * lifecycle transitions. When the JS context is torn down mid-navigation
 * (e.g. WKWebView "provisional navigation failed" / "Frame load interrupted")
 * the bridge can be missing and the direct dereference throws:
 *   TypeError: undefined is not an object (evaluating 'window.Capacitor.triggerEvent')
 *
 * These helpers never assume the bridge exists. They also never throw.
 * A parallel inline shim in index.html installs a no-op `triggerEvent`
 * before any other script so the native side cannot crash the JS context
 * even if this module has not loaded yet.
 */

type CapacitorLike = {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
  triggerEvent?: (eventName: string, target: string, eventData?: unknown) => boolean;
};

export function getCapacitor(): CapacitorLike | undefined {
  if (typeof window === "undefined") return undefined;
  const cap = (window as unknown as { Capacitor?: CapacitorLike }).Capacitor;
  return cap && typeof cap === "object" ? cap : undefined;
}

export function isNativePlatform(): boolean {
  const cap = getCapacitor();
  try {
    return !!(cap && typeof cap.isNativePlatform === "function" && cap.isNativePlatform());
  } catch {
    return false;
  }
}

export function getPlatform(): "ios" | "android" | "web" {
  const cap = getCapacitor();
  try {
    if (cap && typeof cap.getPlatform === "function") {
      const p = cap.getPlatform();
      if (p === "ios" || p === "android") return p;
    }
  } catch {}
  return "web";
}

/**
 * Safe wrapper around `window.Capacitor.triggerEvent`. Prefer official
 * `@capacitor/app` / plugin APIs for real lifecycle handling — this helper
 * exists so any straggling call site cannot crash if the bridge is absent.
 */
export function safeTriggerEvent(eventName: string, target = "document", data?: unknown): boolean {
  const cap = getCapacitor();
  if (!cap || typeof cap.triggerEvent !== "function") {
    try {
      console.info("[Capacitor] triggerEvent skipped — bridge unavailable:", eventName, target);
    } catch {}
    return false;
  }
  try {
    return cap.triggerEvent(eventName, target, data) ?? false;
  } catch (e) {
    try {
      console.warn("[Capacitor] triggerEvent threw:", eventName, e);
    } catch {}
    return false;
  }
}
